import { useMemo, useCallback } from "react";
import { useFriends } from "./useFriends";
import { normalizeEmail } from "../lib/utils";

/**
 * Determines the friend status for a given email.
 * Returns "friend", "pending_outgoing", "pending_incoming", or "none".
 */
export function useFriendStatus() {
  const { friends, outgoingRequests, incomingRequests } = useFriends();

  const friendSet = useMemo(
    () => new Set(friends.map((e) => normalizeEmail(e)).filter(Boolean)),
    [friends]
  );

  const outgoingSet = useMemo(
    () =>
      new Set(
        outgoingRequests
          .map((r) => normalizeEmail(r.toEmail))
          .filter(Boolean)
      ),
    [outgoingRequests]
  );

  const incomingSet = useMemo(
    () =>
      new Set(
        incomingRequests
          .map((r) => normalizeEmail(r.fromEmail))
          .filter(Boolean)
      ),
    [incomingRequests]
  );

  const getStatus = useCallback(
    (email) => {
      const normalized = normalizeEmail(email);
      if (!normalized) return "none";
      if (friendSet.has(normalized)) return "friend";
      if (outgoingSet.has(normalized)) return "pending_outgoing";
      if (incomingSet.has(normalized)) return "pending_incoming";
      return "none";
    },
    [friendSet, outgoingSet, incomingSet]
  );

  return { getStatus };
}
